import { Sparkles } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  clearPwaUpdate,
  subscribePwaUpdate,
  type PwaUpdate,
} from "@/lib/pwaUpdate";
import "@/styles/app-update.css";

/** Exit animation length (styles/app-update.css), then it unmounts. */
export const APP_UPDATE_EXIT_MS = 260;

/**
 * "A new version is available" notice. On a phone it is a compact floating
 * liquid-glass toast just above the tab bar (clear of the header, the safe
 * areas and the iOS vp-gap strip), springing in and sinking out; from `md`
 * up it keeps its original top-centred card. See styles/app-update.css.
 */
export const AppUpdateNotice = () => {
  const translate = useTranslate();
  const [update, setUpdate] = useState<PwaUpdate | null>(null);
  // The last update shown, kept while the notice animates out.
  const [shown, setShown] = useState<PwaUpdate | null>(null);

  useEffect(
    () =>
      subscribePwaUpdate((next) => {
        // next is itself a function. Pass it through an updater so React
        // stores it instead of calling it.
        setUpdate(() => next);
        if (next) setShown(() => next);
      }),
    [],
  );

  const leaving = update == null && shown != null;
  useEffect(() => {
    if (!leaving) return;
    const timer = window.setTimeout(() => setShown(null), APP_UPDATE_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);

  const current = update ?? shown;
  if (!current) return null;

  return (
    <div
      data-state={leaving ? "closed" : "open"}
      className="app-update pointer-events-none fixed inset-x-0 top-0 z-[80] flex justify-center p-3 pt-[max(0.75rem,env(safe-area-inset-top))]"
    >
      <div
        role="status"
        className="app-update-card pointer-events-auto flex w-full max-w-md flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3 text-card-foreground shadow-card"
      >
        <span aria-hidden className="app-update-icon md:hidden">
          <Sparkles className="size-4" strokeWidth={2.25} />
        </span>
        <p className="app-update-text min-w-40 flex-1 text-sm font-medium">
          {translate("crm.pwa.update_available")}
        </p>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="app-update-later"
          disabled={leaving}
          onClick={() => clearPwaUpdate()}
        >
          {translate("crm.pwa.later")}
        </Button>
        <Button
          type="button"
          size="sm"
          className="app-update-accept"
          disabled={leaving}
          onClick={() => void current()}
        >
          {translate("crm.pwa.update")}
        </Button>
      </div>
    </div>
  );
};
